import { useCallback, useEffect, useMemo } from "react";
import {
  Control,
  Controller,
  FieldValues,
  FormProvider,
  Path,
  RegisterOptions,
  useForm,
  UseFormRegister
} from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { CloseIcon } from "@/assets/icons";
import BadgeDataType from "@/components/ui/badge-data-type";
import Dropdown from "@/components/ui/dropdown";
import { IOptionItem } from "@/components/ui/dropdown/types";
import FormField from "@/components/ui/formField";
import RadioButton from "@/components/ui/radio";
import { MESSAGE } from "@/constants/message";
import { GEOHASH_REGEX } from "@/constants/regex";
import { useMap } from "@/hooks/map/core/useMapContext";
import useSessionStorage from "@/hooks/store/useSessionStorage";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import { RootState } from "@/store";
import { clearDrawPolygonDataset } from "@/store/slice/spatialSlice";
import {
  enabledCustomDrawPolygonDataset,
  enableDrawPolygon,
  resetShowPanelDatasetDetail
} from "@/store/slice/uiSlice";
import { SpatialAggregationType } from "@/types/common";
import {
  DataSchemaType,
  DatasetAttributeSchema,
  DatasetSchemaLabel
} from "@/types/dataset";
import { convertFormatterDayjs } from "@/utils/date";
import { removeSpecialCharacters } from "@/utils/string";

import { Button } from "equator-design-system";
import { isValidCell } from "h3-js";

import { DatasetDetailRow } from "../columns";
import {
  DRAW_POLYGON_INPUT,
  GEOJSON_INPUT,
  LATITUDE_INPUT,
  LONGITUDE_INPUT,
  WKT_INPUT
} from "./constant";
import EFormatGeom, { GeometryForm } from "./GeometryForm";

interface FormActionPanelProps {
  fields: DatasetAttributeSchema[];
  initialValues?: DatasetDetailRow | null;
  onSubmit: (
    data: Record<string, unknown>,
    originalData?: DatasetDetailRow | null
  ) => void;
  spatialAggregationType?: SpatialAggregationType | null;
}

interface FieldDatasetsProps<T extends FieldValues> {
  type: DataSchemaType;
  name: Path<T>;
  register: UseFormRegister<T>;
  rules: RegisterOptions<T>;
  error?: string;
  control: Control<T>;
  label?: string;
  isMandatory?: boolean;
  placeHolder?: string;
}

/**
 * Derives the stable form-field key for a schema field.
 * Prefers `field.code` when present; falls back to a sanitised
 * version of `attribute_name` so that react-hook-form always
 * receives a defined, special-character-free string.
 */
export const getFieldKey = (field: DatasetAttributeSchema): string =>
  field.code || removeSpecialCharacters(field.attribute_name);

/**
 * Determines whether a schema field must be filled in before the form
 * can be submitted.
 *
 * A field is mandatory when:
 * - `is_nullable` is false (the column does not accept null values), or
 * - `is_reference_geo` is true, or
 * - `is_spatial` is true.
 *
 * When `is_nullable` is true (and the field is neither a reference geo nor
 * spatial), the field is optional.
 */
export const isFieldMandatory = (field: DatasetAttributeSchema): boolean =>
  !field.is_nullable || field.is_reference_geo || field.is_spatial;

const BadgeIcon = ({ type }: { type: DataSchemaType }) => {
  return <BadgeDataType type={type} />;
};

export interface DatasetDetailSessionStorageProps {
  id?: string;
  dataFields?: DatasetAttributeSchema[];
  data?: Record<string, unknown>;
  type?: "add" | "edit";
  image?: Record<string, string>;
  originalData?: DatasetDetailRow | null;
  name?: string;
  label?: string;
  // Serialised react-hook-form errors captured before navigating to the
  // draw-polygon flow, so they can be restored when the form re-mounts.
  formErrors?: Record<string, { type?: string; message?: string }>;
}

const FieldDatasets = <T extends FieldValues>({
  type,
  name,
  register,
  rules,
  error,
  control,
  label,
  isMandatory = false,
  placeHolder = ""
}: FieldDatasetsProps<T>) => {
  switch (type) {
    case DataSchemaType.BOOL: {
      const booleanOption: IOptionItem[] = [
        { label: "True", value: "true" },
        { label: "False", value: "false" }
      ];

      return (
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field: { onChange, value, onBlur } }) => (
            <div className="flex flex-col gap-1 w-full">
              <Dropdown
                className="text-sm"
                value={
                  booleanOption?.find((c) => c.value === String(value)) || null
                }
                aria-label={name}
                data={booleanOption}
                placeholder={name}
                labelBorder={label ?? name}
                required={isMandatory}
                isValidationError={!!error}
                onSelect={(val) => {
                  onChange(val?.value ?? "");
                  onBlur();
                }}
                suffix={<BadgeIcon type={type} />}
              />
              {error && (
                <p className="px-2 text-[10px] text-cinnabar self-start">
                  {error}
                </p>
              )}
            </div>
          )}
        />
      );
    }
    case DataSchemaType.DATE:
      return (
        <FormField
          name={name}
          register={register}
          placeHolder={placeHolder}
          suffix={<BadgeIcon type={type} />}
          rules={rules}
          error={error}
          type="date"
          label={label}
          required={isMandatory}
        />
      );
    case DataSchemaType.TIME:
      return (
        <FormField
          name={name}
          register={register}
          placeHolder={placeHolder}
          suffix={<BadgeIcon type={type} />}
          rules={rules}
          error={error}
          type="time"
          label={label}
          required={isMandatory}
        />
      );
    case DataSchemaType.DATE_TIME:
      return (
        <FormField
          name={name}
          register={register}
          placeHolder={placeHolder}
          suffix={<BadgeIcon type={type} />}
          rules={rules}
          error={error}
          type="datetime-local"
          label={label}
          required={isMandatory}
        />
      );
    default:
      return (
        <FormField
          name={name}
          register={register}
          placeHolder={placeHolder}
          suffix={<BadgeIcon type={type} />}
          rules={rules}
          error={error}
          label={label}
          required={isMandatory}
        />
      );
  }
};

export const FormActionPanel = ({
  fields,
  initialValues,
  onSubmit,
  spatialAggregationType
}: FormActionPanelProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    sessionValue: sessionStorageData,
    setSessionItem: setItem,
    removeSessionItem: removeItem
  } = useSessionStorage(
    "dataset-detail-draw-polygon",
    {} as DatasetDetailSessionStorageProps
  );
  const existingFields: DatasetAttributeSchema[] =
    sessionStorageData?.dataFields ?? fields;

  const { drawnPolygon } = useSelector((state: RootState) => state.spatial);

  const { showConfirmation } = useConfirmationModal();
  const { enterDrawMode } = useMap();

  // Helper function to get geometry-related session data
  const getGeomSession = (attr: string, session: Record<string, unknown>) => {
    const result: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(session)) {
      if (!value) continue;

      // Match the full `_<attr>` suffix so keys with underscores (e.g.
      // "geom_wkt") still resolve.
      if (key.endsWith(`_${attr}`)) {
        result[key] = value;
      }
    }

    return result;
  };

  // Persisted react-hook-form errors restored after the draw-polygon round
  // trip. Memoised so the reference is stable across renders: the `errors`
  // option below is *controlled* by RHF, so handing it a fresh object every
  // render makes RHF re-apply it endlessly and locks the component in an
  // infinite re-render loop.
  const persistedErrors = useMemo(() => {
    if (!sessionStorageData?.formErrors) return undefined;
    return Object.fromEntries(
      Object.entries(sessionStorageData.formErrors)
        .filter(([, err]) => err?.message || err?.type)
        .map(([name, err]) => [
          name,
          { type: err.type ?? "manual", message: err.message }
        ])
    );
  }, [sessionStorageData?.formErrors]);

  const methods = useForm({
    mode: "onChange",
    defaultValues: existingFields.reduce(
      (acc, field) => {
        // Use getFieldKey so the accumulator key is always a defined string,
        // regardless of whether field.code is present or not.
        const key = getFieldKey(field);

        const sessionValue = sessionStorageData?.data?.[key];
        // Session values are already in browser-input format, so skip the
        // backend-format conversion below (only initialValues need it).
        const isFromSession =
          sessionValue !== undefined && sessionValue !== null;
        const rawValue = sessionValue ?? initialValues?.[key];

        const safeBooleanValue =
          typeof rawValue === "boolean" ? String(rawValue) : rawValue;

        if (safeBooleanValue === undefined || safeBooleanValue === null) {
          acc[key] = "";
          return acc;
        }

        if (field.data_type === DataSchemaType.DATE) {
          acc[key] = isFromSession
            ? (safeBooleanValue as string)
            : convertFormatterDayjs(
                safeBooleanValue as string,
                "YYYY-MM-DD",
                DataSchemaType.DATE,
                field.time_format
              );
        } else if (field.data_type === DataSchemaType.DATE_TIME) {
          // input type="datetime-local" => YYYY-MM-DDTHH:mm
          acc[key] = isFromSession
            ? (safeBooleanValue as string)
            : convertFormatterDayjs(
                safeBooleanValue as string,
                "YYYY-MM-DDTHH:mm",
                DataSchemaType.DATE_TIME,
                field.time_format
              );
        } else if (field.data_type === DataSchemaType.TIME) {
          // input type="time" => HH:mm
          acc[key] = isFromSession
            ? (safeBooleanValue as string)
            : convertFormatterDayjs(safeBooleanValue as string, "HH:mm");
        } else if (field.data_type === DataSchemaType.GEOMETRY) {
          const geomSession = getGeomSession(
            key,
            sessionStorageData?.data || {}
          );
          const formatGeom =
            Object.keys(geomSession).length || safeBooleanValue;

          if (Object.keys(geomSession).length) {
            const geomEntries = Object.entries(geomSession);
            for (const [geomKey, value] of geomEntries) {
              acc[geomKey] = value;
            }
          } else {
            acc[`wkt_geom_input_${key}`] = safeBooleanValue;
          }

          acc[key] = safeBooleanValue;
          acc[`format_geom_${key}`] = formatGeom
            ? sessionStorageData?.data?.[`format_geom_${key}`] || "wkt"
            : "";
        } else {
          acc[key] = safeBooleanValue;
        }

        return acc;
      },
      {} as Record<string, unknown>
    ),
    errors: persistedErrors
  });

  const {
    register,
    control,
    formState: { isValid, errors, isDirty, dirtyFields },
    handleSubmit,
    watch,
    getValues,
    setValue,
    resetField,
    reset,
    trigger
  } = methods;

  const handleConfirmClose = useCallback(() => {
    removeItem();
    dispatch(resetShowPanelDatasetDetail());
    dispatch(clearDrawPolygonDataset());
  }, [dispatch, removeItem]);

  const generatePattern = (dataType: DataSchemaType) => {
    switch (dataType) {
      case DataSchemaType.INT:
        return {
          pattern: /^[+-]?\d+$/,
          message: "Only numeric values are allowed"
        };
      case DataSchemaType.FLOAT:
      case DataSchemaType.LATITUDE:
      case DataSchemaType.LONGITUDE:
      case DataSchemaType.PERCENTAGE:
      case DataSchemaType.RATIO:
        return {
          pattern: /^[+-]?(\d*[.])?\d+$/,
          message: "Only numeric values are allowed"
        };
      case DataSchemaType.BOOL:
        return { pattern: /^(true|false)$/i, message: "" };
      default:
        return { pattern: /.*/, message: "" };
    }
  };

  const generateValidate = (value: string, dataLabel?: DatasetSchemaLabel) => {
    if (!value) return true;
    if (dataLabel === DatasetSchemaLabel.H3 && !isValidCell(value)) {
      return "Invalid H3 index. Please provide a valid H3 index.";
    }
    if (
      dataLabel === DatasetSchemaLabel.GEOHASH &&
      !GEOHASH_REGEX.test(value)
    ) {
      return "Invalid Geohash. Please provide a valid Geohash.";
    }
    return true;
  };

  const titlePanel =
    initialValues || sessionStorageData?.type === "edit"
      ? "Edit Row"
      : "Add New Row";
  const submitButton =
    initialValues || sessionStorageData?.type === "edit" ? "Update" : "Create";
  const isEdit = !!initialValues || sessionStorageData?.type === "edit";
  const disableSubmit = !isValid || (isEdit && !isDirty);

  const handleClose = useCallback(() => {
    if (Object.keys(dirtyFields).length) {
      showConfirmation({
        title: "Unsaved Changes",
        message: MESSAGE.CONFIRMATION_LEAVE,
        cancelLabel: "Cancel",
        confirmLabel: "Leave",
        onConfirm: handleConfirmClose
      });
    } else handleConfirmClose();
  }, [showConfirmation, dirtyFields, handleConfirmClose]);

  const fieldsWithoutGeometry = fields.filter(
    (field) => field.data_type !== DataSchemaType.GEOMETRY
  );

  const onClearGeomValue = (type: EFormatGeom, name: string) => {
    const removeItems = Object.values(EFormatGeom).filter(
      (value) => value !== type
    );
    for (const item of removeItems) {
      if (item === EFormatGeom.COORDINATES) {
        setValue(`${LATITUDE_INPUT}${name}`, "");
        setValue(`${LONGITUDE_INPUT}${name}`, "");
      } else if (item === EFormatGeom.WKT) {
        setValue(`${WKT_INPUT}${name}`, "");
      } else if (item === EFormatGeom.GEOJSON) {
        setValue(`${GEOJSON_INPUT}${name}`, "");
      } else if (item === EFormatGeom.DRAW_POLYGON) {
        const newImage = { ...sessionStorageData?.image };
        if (newImage && name in newImage) {
          delete newImage[name];
        }
        setItem({
          ...sessionStorageData,
          image: newImage
        });
        setValue(`${DRAW_POLYGON_INPUT}${name}`, "");
        dispatch(clearDrawPolygonDataset(name));
      }
    }
  };

  const onDeleteDraw = useCallback(
    (name: string) => {
      const newImage = { ...sessionStorageData?.image };
      if (newImage && name in newImage) {
        delete newImage[name];
      }
      setItem({
        ...sessionStorageData,
        image: newImage
      });
      dispatch(clearDrawPolygonDataset(name));
      resetField(sessionStorageData?.name || "");
    },
    [dispatch, resetField, sessionStorageData, setItem]
  );

  useEffect(() => {
    // To show a confirmation dialog when the user tries to leave the page
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      handleConfirmClose();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [handleConfirmClose]);

  useEffect(() => {
    if (!drawnPolygon) return;

    for (const [name, polygon] of Object.entries(drawnPolygon)) {
      const key = `draw_polygon_${name}`;
      const value = JSON.stringify(polygon.geometry);

      setValue(key, value, { shouldDirty: true, shouldTouch: true });
    }
  }, [drawnPolygon, setValue, register, getValues, reset]);

  /* When the form re-mounts with values restored from session storage (e.g.
   * after returning from the draw-polygon flow), re-run validation so `isValid`
   * and the error state reflect those values. react-hook-form's `onChange` mode
   * does not validate untouched fields on mount, so without this the submit
   * button can stay disabled (or enabled) incorrectly until the user edits a
   * field. The `data` guard keeps a fresh "Add" form from being force-validated,
   * which would otherwise surface errors before the user has typed anything.
   */
  useEffect(() => {
    if (!sessionStorageData?.data) return;
    trigger();
  }, [sessionStorageData, trigger]);

  const handleDraw = useCallback(
    (name: string) => {
      // Implement the logic for drawing a polygon here
      dispatch(enableDrawPolygon());
      dispatch(enabledCustomDrawPolygonDataset());
      enterDrawMode();

      setItem({
        data: getValues(),
        dataFields: fields,
        type: isEdit ? "edit" : "add",
        image: sessionStorageData?.image,
        originalData: initialValues || sessionStorageData?.originalData,
        name: name,
        id: id,
        // Persist the current validation errors so they can be restored
        // (same red borders/messages) when the form re-mounts on return.
        formErrors: Object.fromEntries(
          Object.entries(errors).map(([key, value]) => [
            key,
            { type: value?.type as string | undefined, message: value?.message }
          ])
        )
      });
      navigate("/");
    },
    [
      dispatch,
      enterDrawMode,
      errors,
      fields,
      getValues,
      id,
      initialValues,
      isEdit,
      navigate,
      sessionStorageData,
      setItem
    ]
  );

  const originalData = initialValues ?? sessionStorageData?.originalData;

  return (
    <div className="absolute top-4 right-4 z-[9] border-gainsboro bg-white rounded-lg w-[456px] h-[calc(100vh-32px)] overflow-y-auto flex flex-col">
      <div className="px-[24px] py-4">
        <div className="flex flex-row items-center justify-between">
          <h2 className="text-txtHeading font-sans font-bold text-[20px]">
            {titlePanel}
          </h2>
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="p-0 bg-transparent border-0"
              aria-label={`Close ${titlePanel} Panel`}
            >
              <CloseIcon className="fill-dim-gray" />
            </button>
          </div>
        </div>
      </div>
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit((data) => onSubmit(data, originalData))}
          className="flex flex-col flex-1"
        >
          <div className="flex flex-col flex-1 gap-4 px-4">
            {fieldsWithoutGeometry.map((field) => {
              const fieldKey = getFieldKey(field);
              return (
                <FieldDatasets
                  name={fieldKey}
                  key={field.id}
                  register={register}
                  rules={{
                    required: isFieldMandatory(field)
                      ? `${removeSpecialCharacters(field.attribute_name).toLocaleLowerCase()} is required`
                      : false,
                    pattern: {
                      value: generatePattern(field.data_type).pattern,
                      message: generatePattern(field.data_type).message
                    },
                    validate: (value) =>
                      generateValidate(value as string, field?.data_label)
                  }}
                  error={errors[fieldKey]?.message}
                  type={field.data_type}
                  control={control}
                  label={removeSpecialCharacters(field.attribute_name)}
                  isMandatory={isFieldMandatory(field)}
                  placeHolder={removeSpecialCharacters(field.attribute_name)}
                />
              );
            })}
            {fields
              .filter((field) => field.data_type === DataSchemaType.GEOMETRY)
              .map((field) => {
                const fieldKey = getFieldKey(field);
                return (
                  <GeometryForm
                    key={fieldKey}
                    formatGeom={watch(`format_geom_${fieldKey}`) as EFormatGeom}
                    register={register}
                    name={fieldKey}
                    control={control}
                    onDraw={() => handleDraw(fieldKey)}
                    mapImg={sessionStorageData?.image?.[fieldKey]}
                    drawPolygonState={drawnPolygon?.[fieldKey]}
                    onDeleteDraw={onDeleteDraw}
                    onClearGeomValue={onClearGeomValue}
                    type={field.data_label}
                    isMandatory={isFieldMandatory(field)}
                  />
                );
              })}
            {spatialAggregationType === "adm_area" && (
              <div className="flex flex-col gap-3">
                <Controller
                  name="admin_area_level"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, value } }) => (
                    <div className="flex flex-col gap-3 mt-4">
                      <p className="text-sm text-txtHeading font-semibold">
                        Attribute Selection
                      </p>
                      <span className="text-xs">
                        Please select one get the geometric value of the
                        administrative area.
                      </span>
                      {fieldsWithoutGeometry.map((item) => {
                        const itemKey = getFieldKey(item);
                        return (
                          <RadioButton
                            key={itemKey}
                            name={itemKey}
                            checked={value === itemKey}
                            onCheck={() => onChange(itemKey)}
                            label={removeSpecialCharacters(item.attribute_name)}
                            inputClassName="w-5 h-5 border-dim-gray"
                            labelClassName="text-xs font-normal text-txtHeading"
                          />
                        );
                      })}
                    </div>
                  )}
                />
              </div>
            )}
          </div>

          <div className="flex px-4 justify-center gap-2 bg-white w-full py-[20px] rounded-t-none rounded-lg">
            <Button
              variant="secondary"
              className="h-[40px] w-[198px] btn-secondary text-sm !font-bold"
              onClick={handleClose}
              type="button"
            >
              <span>Cancel</span>
            </Button>
            <Button
              className="h-[40px] w-[198px] btn-primary text-sm !font-bold"
              disabled={disableSubmit}
              type="submit"
            >
              <span>{submitButton}</span>
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
